import { useEffect, useState } from "react"
import { Card, CardContent } from "../components/ui/card"
import { Button } from "../components/ui/button"
import { Input } from "../components/ui/input"
import { Badge } from "../components/ui/badge"
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../components/ui/table"
import { adminApi } from "../services/adminApi"
import { toast } from "sonner"
import { X, Plus, Trash2 } from "lucide-react"

interface NativeAbility {
  id: string
  name: string
  description: string
  instructions: string
  icon?: string
  toolPreferences?: string[]
}

type ModalMode = "create" | "edit"

export default function AbilitiesPage() {
  const [abilities, setAbilities] = useState<NativeAbility[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [modalMode, setModalMode] = useState<ModalMode | null>(null)
  const [editingAbility, setEditingAbility] = useState<NativeAbility | null>(null)
  const [saving, setSaving] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  // Form state
  const [formId, setFormId] = useState("")
  const [formName, setFormName] = useState("")
  const [formDescription, setFormDescription] = useState("")
  const [formInstructions, setFormInstructions] = useState("")
  const [formIcon, setFormIcon] = useState("")
  const [formToolPreferences, setFormToolPreferences] = useState("")

  useEffect(() => {
    fetchAbilities()
  }, [])

  const fetchAbilities = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await adminApi.getNativeAbilities()
      setAbilities(Array.isArray(data) ? data : [])
    } catch (e: any) {
      console.error(e)
      const msg = e?.response?.data?.error || "Failed to load abilities"
      setError(msg)
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }

  const openCreate = () => {
    setModalMode("create")
    setEditingAbility(null)
    setFormId("")
    setFormName("")
    setFormDescription("")
    setFormInstructions("")
    setFormIcon("")
    setFormToolPreferences("")
  }

  const openEdit = (ability: NativeAbility) => {
    setModalMode("edit")
    setEditingAbility(ability)
    setFormId(ability.id)
    setFormName(ability.name || "")
    setFormDescription(ability.description || "")
    setFormInstructions(ability.instructions || "")
    setFormIcon(ability.icon || "")
    setFormToolPreferences((ability.toolPreferences || []).join(", "))
  }

  const closeModal = () => {
    setModalMode(null)
    setEditingAbility(null)
  }

  const handleSave = async () => {
    const toolPrefs = formToolPreferences
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)

    try {
      setSaving(true)

      if (modalMode === "create") {
        if (!formId.trim() || !formName.trim() || !formDescription.trim() || !formInstructions.trim()) {
          toast.error("ID, name, description, and instructions are required")
          return
        }
        await adminApi.createNativeAbility({
          id: formId.trim(),
          name: formName.trim(),
          description: formDescription.trim(),
          instructions: formInstructions.trim(),
          icon: formIcon || undefined,
          toolPreferences: toolPrefs,
        })
        toast.success("Ability created")
      } else if (modalMode === "edit" && editingAbility) {
        await adminApi.updateNativeAbility(editingAbility.id, {
          name: formName,
          description: formDescription,
          instructions: formInstructions,
          icon: formIcon || undefined,
          toolPreferences: toolPrefs,
        })
        toast.success("Ability updated")
      }

      closeModal()
      fetchAbilities()
    } catch (e: any) {
      console.error(e)
      toast.error(e?.response?.data?.error || "Failed to save ability")
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this native ability? This cannot be undone.")) return
    try {
      setDeletingId(id)
      await adminApi.deleteNativeAbility(id)
      setAbilities((prev) => prev.filter((a) => a.id !== id))
      toast.success("Ability deleted")
    } catch (e: any) {
      console.error(e)
      toast.error(e?.response?.data?.error || "Failed to delete ability")
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Native Abilities</h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchAbilities} disabled={loading}>
            Refresh
          </Button>
          <Button onClick={openCreate}>
            <Plus size={16} className="mr-1.5" />
            Create Ability
          </Button>
        </div>
      </div>

      {error && <div className="text-sm text-red-600">{error}</div>}

      <Card className="shadow-sm border-0 ring-1 ring-gray-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 text-gray-500">Loading...</div>
          ) : abilities.length === 0 ? (
            <div className="p-6 text-gray-500">No native abilities found.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Tool Preferences</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {abilities.map((ability) => (
                  <TableRow key={ability.id}>
                    <TableCell className="font-mono text-xs text-gray-500">
                      {ability.id}
                    </TableCell>
                    <TableCell className="font-medium">{ability.name}</TableCell>
                    <TableCell className="max-w-xs truncate">
                      {ability.description}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(ability.toolPreferences || []).map((tool) => (
                          <Badge key={tool} variant="info">
                            {tool}
                          </Badge>
                        ))}
                        {(!ability.toolPreferences ||
                          ability.toolPreferences.length === 0) && (
                          <span className="text-gray-400 text-xs">None</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEdit(ability)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDelete(ability.id)}
                          disabled={deletingId === ability.id}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      {modalMode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {modalMode === "create" ? "Create Ability" : "Edit Ability"}
              </h2>
              <button
                onClick={closeModal}
                className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            <div className="px-6 py-4 space-y-4">
              {modalMode === "create" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ID
                  </label>
                  <Input
                    value={formId}
                    onChange={(e) => setFormId(e.target.value)}
                    placeholder="e.g. pdf_generator"
                    className="font-mono"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Unique identifier, lowercase with underscores. Cannot be changed later.
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <Input
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="Ability name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <Input
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  placeholder="Short description"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Instructions
                </label>
                <textarea
                  value={formInstructions}
                  onChange={(e) => setFormInstructions(e.target.value)}
                  placeholder="Detailed instructions for the ability..."
                  rows={12}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Icon
                </label>
                <Input
                  value={formIcon}
                  onChange={(e) => setFormIcon(e.target.value)}
                  placeholder="Icon name (optional)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tool Preferences
                </label>
                <Input
                  value={formToolPreferences}
                  onChange={(e) => setFormToolPreferences(e.target.value)}
                  placeholder="Comma-separated tool names, e.g. sandbox, preview"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Tools that auto-load when this ability is activated.
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-200">
              <Button variant="outline" onClick={closeModal} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving
                  ? "Saving..."
                  : modalMode === "create"
                    ? "Create"
                    : "Save Changes"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
